import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { type FC, useState } from "react";
import Markdown from "react-markdown";

import {
  Button,
  Dialog,
  Headline,
  PlaceholderEditor,
  PlaceholderEditorVariant,
  Small,
} from "@fronterahealth/frontera-ui-components";

import {
  type QACriteria,
  createTemplateQaCriteria,
  deleteTemplateQaCriteria,
  updateTemplateQaCriteria,
  useListTemplateQaCriteria,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";

interface TemplateQACriteriaProps {
  templateId: number;
  templateName: string;
}

export const TemplateQACriteria: FC<TemplateQACriteriaProps> = ({ templateId, templateName }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<QACriteria | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<QACriteria | null>(null);

  const { data, isLoading, refetch } = useListTemplateQaCriteria(templateId, {
    query: { queryKey: ["template-qa-criteria", templateId] },
  });

  const criteriaList = data?.status === 200 ? (data.data.results ?? []) : [];

  const createMutation = useMutation({
    mutationFn: (input: { name: string; content: string; category: string }) =>
      createTemplateQaCriteria({ template_id: templateId, ...input }),
    onSuccess: (result) => {
      if (result.status === 201) {
        notifySuccess("Criteria created");
        setShowAddForm(false);
        void refetch();
      } else {
        notifyError("Failed to create criteria");
      }
    },
    onError: () => notifyError("Failed to create criteria"),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: number; name?: string; content?: string; category?: string; enabled?: boolean }) =>
      updateTemplateQaCriteria(input),
    onSuccess: (result) => {
      if (result.status === 200) {
        notifySuccess("Criteria updated");
        setEditingCriteria(null);
        void refetch();
      } else {
        notifyError("Failed to update criteria");
      }
    },
    onError: () => notifyError("Failed to update criteria"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTemplateQaCriteria({ id }),
    onSuccess: () => {
      notifySuccess("Criteria deleted");
      setDeleteTarget(null);
      void refetch();
    },
    onError: () => notifyError("Failed to delete criteria"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border-medium p-12">
        <Small colorType="secondary">Loading...</Small>
      </div>
    );
  }

  const formActive = showAddForm || !!editingCriteria;

  return (
    <div className={formActive ? "pb-24" : ""}>
      <div className="flex items-center justify-between mb-6">
        <Headline size="small">{templateName} — QA Criteria</Headline>
        {!formActive && <Button appearance="primary" onClick={() => setShowAddForm(true)} text="Add Criteria" />}
      </div>

      {showAddForm && (
        <CriteriaForm
          onSubmit={(name, content, category) => createMutation.mutate({ name, content, category })}
          onCancel={() => setShowAddForm(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {criteriaList.length === 0 && !showAddForm ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-border-medium p-12">
          <Small colorType="secondary">No criteria yet. Use "Add Criteria" to create one.</Small>
        </div>
      ) : (
        <div className="space-y-3">
          {criteriaList.map((criteria) => (
            <CriteriaCard
              key={criteria.id}
              criteria={criteria}
              isEditing={editingCriteria?.id === criteria.id}
              onEdit={() => setEditingCriteria(criteria)}
              onDelete={() => setDeleteTarget(criteria)}
              onSave={(updates) => updateMutation.mutate({ id: criteria.id, ...updates })}
              onCancelEdit={() => setEditingCriteria(null)}
              isSaving={updateMutation.isPending}
              onToggleEnabled={(enabled) => updateMutation.mutate({ id: criteria.id, enabled })}
            />
          ))}
        </div>
      )}

      <Dialog
        title={`Delete "${deleteTarget?.name}"?`}
        open={!!deleteTarget}
        setOpen={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        primaryButton={{
          text: "Delete",
          onClick: () => {
            if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          },
          disabled: deleteMutation.isPending,
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setDeleteTarget(null) }}
        size="sm"
      >
        <p className="text-sm text-text-secondary">This will permanently remove this QA criterion.</p>
      </Dialog>
    </div>
  );
};

// --- CriteriaForm ---

interface CriteriaFormProps {
  initialName?: string;
  initialContent?: string;
  initialCategory?: string;
  onSubmit: (name: string, content: string, category: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CriteriaForm: React.FC<CriteriaFormProps> = ({
  initialName = "",
  initialContent = "",
  initialCategory = "",
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [name, setName] = useState(initialName);
  const [content, setContent] = useState(initialContent);
  const [category, setCategory] = useState(initialCategory);

  return (
    <div className="mb-4 rounded-lg border border-border-subtle bg-bg-default p-4 space-y-3">
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Name *</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Grammar Check"
          className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="e.g., Writing Quality"
          className="block w-full rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Content *</label>
        <PlaceholderEditor
          existingContent={initialContent}
          onChange={setContent}
          insertValues={[]}
          variant={PlaceholderEditorVariant.WYSIWYG}
        />
      </div>
      <TemplateSaveFooter
        isDirty
        isSaving={isSubmitting}
        onSave={() => onSubmit(name, content, category)}
        onDiscard={onCancel}
        saveLabel="Save"
        discardLabel="Cancel"
        saveDisabled={!name.trim() || !content.trim()}
      />
    </div>
  );
};

// --- CriteriaCard ---

interface CriteriaCardProps {
  criteria: QACriteria;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updates: { name?: string; content?: string; category?: string }) => void;
  onCancelEdit: () => void;
  isSaving: boolean;
  onToggleEnabled: (enabled: boolean) => void;
}

const CriteriaCard: React.FC<CriteriaCardProps> = ({
  criteria,
  isEditing,
  onEdit,
  onDelete,
  onSave,
  onCancelEdit,
  isSaving,
  onToggleEnabled,
}) => {
  if (isEditing) {
    return (
      <CriteriaForm
        initialName={criteria.name}
        initialContent={criteria.content}
        initialCategory={criteria.category}
        onSubmit={(name, content, category) => onSave({ name, content, category })}
        onCancel={onCancelEdit}
        isSubmitting={isSaving}
      />
    );
  }

  return (
    <div className={`rounded-lg border p-4 bg-bg-surface ${!criteria.enabled ? "opacity-60" : "border-border-subtle"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-text-primary">{criteria.name}</span>
            {criteria.category && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-bg-selected text-text-secondary">
                {criteria.category}
              </span>
            )}
            {!criteria.enabled && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-bg-selected text-text-secondary">
                Disabled
              </span>
            )}
          </div>
          <div className="prose prose-sm max-w-none text-text-secondary overflow-hidden break-words">
            <Markdown>{criteria.content}</Markdown>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onToggleEnabled(!criteria.enabled)}
            className="px-2 py-1 text-xs rounded border border-border-subtle text-text-secondary hover:bg-bg-default"
          >
            {criteria.enabled ? "Disable" : "Enable"}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 rounded text-text-tertiary hover:text-text-secondary hover:bg-bg-default"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded text-text-tertiary hover:text-text-error-primary hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
